import { ChakraProvider, Box, Spinner, extendTheme, Button, Heading, Text, VStack } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import CourseFlowRouter from './router/CourseFlowRouter';
import CourseStepRenderer from './router/CourseStepRenderer';

// Pages
import { LoginPage } from './pages/auth/LoginPage';
import { UpdatePasswordPage } from './pages/auth/UpdatePasswordPage';
import { PreferencesPage } from './pages/preferences/PreferencesPage';
import { ChatPage } from './pages/chat/ChatPage';
import MarkdownTestPage from './pages/test/MarkdownTestPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';

// Extend the default theme to use Lexend font
const theme = extendTheme({
  fonts: {
    heading: '"Lexend", sans-serif',
    body: '"Lexend", sans-serif',
  },
  styles: {
    global: {
      'html, body': {
        fontFamily: '"Lexend", sans-serif',
        lineHeight: 'tall',
        letterSpacing: '0.01em',
      },
      // Improve readability for text elements
      p: {
        marginBottom: '1em',
        lineHeight: '1.6',
      },
    },
  },
  // Add some component-specific styles for better readability
  components: {
    Button: {
      baseStyle: {
        fontWeight: '600',
        borderRadius: 'md',
      },
      variants: {
        solid: (props: any) => ({
          bg: '#C200FB', // RL Purple
          color: 'white',
          _hover: {
            bg: '#A800D6', // Slightly darker purple on hover
            transform: 'translateY(-1px)',
          },
          _active: {
            bg: '#8F00B2', // Even darker purple when pressed
            transform: 'translateY(0)',
          },
        }),
      },
      defaultProps: {
        variant: 'solid',
      },
    },
  },
});

// Define the AppErrorFallback props interface
interface AppErrorFallbackProps {
  error: Error | null;
  resetErrorBoundary: () => void;
}

// Custom error fallback for the app
const AppErrorFallback = ({ error, resetErrorBoundary }: AppErrorFallbackProps) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate('/');
  };

  const handleReload = () => {
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <Box p={6} maxW="container.md" mx="auto" mt={10} textAlign="center">
      <Heading as="h1" size="xl" color="red.500" mb={4}>
        Oops! Something went wrong
      </Heading>
      
      <Text fontSize="lg" mb={6}>
        We're sorry, but the application encountered an unexpected error. 
        Our team has been notified and we're working to fix it.
      </Text>

      <VStack spacing={4} mt={8}>
        <Button 
          colorScheme="blue" 
          onClick={handleGoHome}
          size="lg"
        >
          Return to Home
        </Button>
        
        <Button 
          variant="outline" 
          onClick={handleReload}
          size="lg"
        >
          Reload Application
        </Button>
      </VStack>
    </Box>
  );
};

function AppContent() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Router future={{ v7_relativeSplatPath: true }}>
      <ErrorBoundary 
        fallback={
          <AppErrorFallback 
            error={null} 
            resetErrorBoundary={() => window.location.reload()} 
          />
        }
      >
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/update-password" element={<UpdatePasswordPage />} />
          
          {/* Protected routes */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute user={user}>
                <Layout>
                  <Outlet />
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<CourseFlowRouter />} />
            <Route path="preferences" element={<PreferencesPage />} />
            <Route path="chat" element={<ChatPage />} />
            {/* Dynamic per-course step route */}
            <Route path="s/:slug" element={<CourseStepRenderer />} />
          </Route>

          {/* Test route - can be removed in production */}
          <Route path="/test/markdown" element={<MarkdownTestPage />} />
          
          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </Router>
  );
}

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <CourseProvider>
          <AppContent />
        </CourseProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
